import PageLoader from '@/common/service/flow/page-loader';
import { loadSelectByPersist, plansDataMapper, filterCapacity, loadByShoppingCart } from '@/common/service/plan-data-mapper';
import { pageConfig, flowTypes } from '@/common/service/page-config';
import { isDebug, isEmptyVal } from '@/utils';
import store from '@/store/index';
import _, { cloneDeep } from 'lodash';
import { addToCartByCategoryAndBillingCycle, planQueryParser } from '@/common/service/flow/utils';
import { addParamsIfApm, addParamsIfReferralProgram } from '@/common/service/checkout-utils';
import { pollingEventStatus } from '@/common/polling';
import {
  buyTrialSuccessPage,
  addTrialSuccessPage,
  gotoSuccessPage,
  gotoWiseSuccessPage, getPreAssignPhoneNumber,
} from '@/common/service/common';
import { BILLING_CYCLE, CAMPAIGN_CODES, DEFAULT_FREE_TRIAL_DAYS, DEFAULT_TRIAL_MONTH, DEFAULT_TRIAL_CREDIT, planTypes, zoomOneProMonthlyPlanCodes, zoomOneProYearlyPlanCodes, ABANDON_CART_EMAIL_COOKIE_KEY, VIEWS, ZSCHED_PLAN_CODES } from '@/common/constants';
import Flow from '@/flows/flow';
import {
  getFreeTrialPlansByCampaignName,
  buyFreeTrialPlansForFreeUser,
  buyFreeTrialPlans,
  getPlanMap,
  capacityConfig,
  buySubscription,
  freeUpgrade
} from '@/apis';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { validAddonCycleForBase } from '@/common/service/validate/billing-cycle';
import { APM, WISE } from '@/views/checkout/payment/helper/constant';
import { removeEmptyValue } from '@/common/service/checkout-utils';
import { translation } from '@/plugins/i18n';
import { getLookupRouterParams } from '@/common/service/lookup-params';
import { getPartnershipParams } from '@/views/config/partner-refer/helper/params';
import qs from 'qs';
import { plansof } from '@/utils/chain';
import { modalHybrid } from '@/utils/modal-utils.js';
import { getOrderSummaryFields } from '../utils';
import { loadInitialPlanFlag } from '@/flows/BUY_NOW';
import { ZmMessage } from '@/components/zoom-ui-plus/message/index';
import { addDependentDefaultPbxBasePlan } from '@/common/product-preload';
import { PLAN_TYPES } from '@/views/succezz/utils';
import { addFingerPrintCookie } from '@/utils/fingerprint';

function removeUrlParam(url, paramName) {
  try {
    const urlParts = url.split('?');
    if (urlParts.length >= 2) {
      let prefix = encodeURIComponent(paramName) + '=';
      let params = urlParts[1].split(/[&;]/g);

      for (let i = params.length; i-- > 0; ) {
        if (params[i].lastIndexOf(prefix, 0) !== -1) {
          params.splice(i, 1);
        }
      }

      url = urlParts[0] + (params.length > 0 ? '?' + params.join('&') : '');
    }
    return url;
  } catch (_) {
    return url;
  }
}

export const freeTrialIneligibleCheck = () => {
  const isLoggedIn = window?.SB?.loggedIn;
  const { freeTrialErrorType = '', from = '', from_sub = '', plan = '' } = pageConfig || {};
  if (!freeTrialErrorType) {
    return null;
  }
  try {
    const planType = getPlanTypeByCategory(from || from_sub || plan);
    let errorMessage = '';
    const planTypeTitleMap = {
      [planTypes.ZOOM_MEETING]: 'billing.planform.title.zoom_one_pro',
      [planTypes.ZOOM_SCHEDULER]: 'billing.scheduler.title',
      [planTypes.EVENT_SAAS_CORE]: 'billing.planform.title.eventsaascore',
      [planTypes.VIDEO_SDK_UC]: 'billing.video_sdk_uc.title',
      [planTypes.ZOOM_PHONE]: 'billing.planform.title.zoomphone',
    };
    const freeTrialTitle = planTypeTitleMap[planType]
      ? translation('billing.trial.title', [translation(planTypeTitleMap[planType])])
      : '';
    const title = planTypeTitleMap[planType] ? translation(planTypeTitleMap[planType]) : '';
    const user = window.SB?.user?.email || '';
    errorMessage = isLoggedIn
      ? translation('billing.trial.ineligible_hint', [user, freeTrialTitle, title])
      : translation('billing.trial.ineligible_hint_nn', [translation(planTypeTitleMap[planType])]);
    ZmMessage.error({
      duration: 50000,
      message: errorMessage,
      showClose: true,
      dangerouslyUseHTMLString: true,
    });
    // clear mark
    const newUrl = removeUrlParam(window?.location?.href || '', 'freeTrialErrorType');
    if (newUrl) {
      window?.history?.pushState({}, '', newUrl);
    }
  } catch (e) {
    // no-op
  }

  return true;
};

const freeTrialErrorRedirection = (planType, flowType) => {
  switch (planType) {
    case planTypes.ZOOM_SCHEDULER:
      return `/opc/buy?plan=zsched&period=monthly&from=zsched&originalFlowType=${flowType}&freeTrialErrorType=zsched`;
    case planTypes.ZOOM_MEETING:
      return `/opc/buy/config?plan=pro&subPlan=allSubPlan&type=basic2pro&originalFlowType=${flowType}&freeTrialErrorType=pro`;
    case planTypes.VIDEO_SDK_UC:
      return `/opc/buy/config?plan=zsdkuc&usageType=business&freeTrialErrorType=zsdkuc`;
    case planTypes.ZOOM_PHONE:
      return `/opc/buy?plan=pbx&subPlan=newsubs&from_sub=pbx&from=pbx_ZVU&type=add&majorPlanHost=1&period=annual&quantity=1&usageType=business&originalFlowType=${flowType}&freeTrialErrorType=pbx`;
    case planTypes.EVENT_SAAS_CORE:
      return `/opc/buy?plan=pbx&subPlan=newsubs&from_sub=eventsaascore&from=eventsaascore500&type=add&usageType=business&originalFlowType=${flowType}&freeTrialErrorType=eventsaascore`;
    default:
      return '/billing';
  }
};

export const freeTrialErrorMessage = (errorMessage = '', flowType = flowTypes.buyTrial) => {
  const { from = '', from_sub = '' } = pageConfig || {};
  try {
    const planType = getPlanTypeByCategory(from || from_sub);
    location.replace(freeTrialErrorRedirection(planType, flowType));
  } catch (e) {
    ZmMessage.$message({
      type: 'info',
      duration: 50000,
      message: errorMessage,
      showClose: true,
      iconClass: 'zm-icon-info',
      dangerouslyUseHTMLString: true,
      customClass: 'opc-global-info-message',
    });
  }
};

const checkSelectedPlan = (planMapFlat = []) => {
  return planMapFlat.find((item) => item.cartInfo.selected_number > 0);
};

const filterDisabledPlans = (allPlanResult = {}) => {
  const { disablePlanCodeList = [], disablePlanCategoryList = [] } = allPlanResult;

  return (planMapFlat = []) => {
    return planMapFlat.filter(
      (p) => !disablePlanCodeList.includes(p?.plan_code) && !disablePlanCategoryList.includes(p?.parent_category),
    );
  };
};

const filterFreeTrialPlansFromPlanMap = (freeTrialPlanList = [], campaignCode) => {
  return (planList = []) => {
    return planList.map((item) => {
      const plan = { ...item };
      // filter all plans with free trial in freeTrialPlanList
      const freeTrialPlan = freeTrialPlanList.find((free) => {
        return [plan.plan_code, plan?.priceChangeMappingPlanCode, plan?.ftPlanCode].includes(free.planCode)
      });
      if (freeTrialPlan) {
        plan.isOnlineFreeTrial = true;
        plan.trialDays = freeTrialPlan.trialDays || DEFAULT_FREE_TRIAL_DAYS;
        plan.trialCode = freeTrialPlan.trialCode || '';
        plan.campaignCode = campaignCode || '';
        plan.trialMaxQuantity = freeTrialPlan.trialMaxQuantity || 1;
        if(freeTrialPlan?.freeTrialRatePlan){
          plan.credit = freeTrialPlan.credit || DEFAULT_TRIAL_CREDIT;
          plan.plan_code = freeTrialPlan.planCode;
          plan.trialMonth = freeTrialPlan.trialMonth || DEFAULT_TRIAL_MONTH;
          plan.freeTrialEndDate = freeTrialPlan?.freeTrialEndDate || '';
        }
      }
      return plan;
    });
  };
};

const loadDefaultSelectByQuery = () => {
  return (planList = []) => {
    const hasSelectedPlan = checkSelectedPlan(planList);
    if (hasSelectedPlan) return planList;
    let { from, from_sub, period = BILLING_CYCLE.ANNUAL, quantity = 1 } = pageConfig || {};
    const freeTrialPlanList = planList.filter((p) => !!p.isOnlineFreeTrial); // filter free trial plan only
    const { category_codes, fromType } = planQueryParser(from || from_sub);
    // add target plan to shopping cart
    category_codes.forEach((category) => {
      addToCartByCategoryAndBillingCycle(
        freeTrialPlanList,
        getPlanTypeByCategory(category) || fromType,
        category,
        period,
        quantity,
      );
    });

    addDependentDefaultPbxBasePlan(planList);
    loadInitialPlanFlag(planList)
    return planList;
  };
};

const loadSelectByPersistForBuyTrial = (persist) => {
  // ZOOM-981784 The schduler free trail plan was incorrectly displayed together with other paid plans in OPC flow
  let { from } = pageConfig || {};
  if (from === 'zsched') {
    for (const zschedPlanCode of ZSCHED_PLAN_CODES) {
      if (persist[zschedPlanCode]) {
        return loadSelectByPersist({
          [zschedPlanCode]: persist[zschedPlanCode]
        })
      }
    }
  }
  return loadSelectByPersist(persist)
}

const initForCoupon = () => {
  store.watch((state, getters) => getters.forNewSubscriptionFlow, () => {
    if (!store.getters.forNewSubscriptionFlow) {
      store.commit('resetCouponState')
    }
  })
}

const initShoppingCart = async (state) => {
  const { persist } = state;
  // Add FingerPrint cookie for freetrial flow
  await addFingerPrintCookie();

  // load data
  const { result: planMap = {} } = await getPlanMap();
  const { result: capacityList } = await capacityConfig({ flowType: flowTypes.buyTrial });
  const { campaign_code = '' } = pageConfig || {};
  const { result: freeTrialPlanList = [], errorMessage = '' } = await getFreeTrialPlansByCampaignName(campaign_code);
  const editPlanType = pageConfig?.editConfig?.planType;
  const isZpAicBuyTrial = editPlanType === planTypes.ZOOM_PHONE$AICONCIERGE;
  if (errorMessage && !isZpAicBuyTrial) {
    freeTrialErrorMessage(errorMessage);
  }
  if (pageConfig?.editConfig?.planType === PLAN_TYPES.ZOOM_PHONE) {
    const isZoomPhoneFT = freeTrialPlanList.find(p => getPlanTypeByCategory(p.category) === PLAN_TYPES.ZOOM_PHONE);
    if (isZoomPhoneFT && !window.newBillingConfig?.enableZoomPhoneFreeTrialBuy) {
      location.replace(`/opc/buy?plan=pbx&period=annual&from=pbx_ZVU&addon_period=annual&quantity=1&usageType=business&originalFlowType=${flowTypes.buyTrial}&freeTrialErrorType=pbx`);
    }
  }

  const loader = new PageLoader(planMap);
  loader.decorate('plansDataMapper', plansDataMapper);
  loader.decorate('filterDisabledPlans', filterDisabledPlans(planMap));
  loader.decorate('filterCapacity', filterCapacity(capacityList));
  loader.decorate('filterFreeTrialPlansFromPlanMap', filterFreeTrialPlansFromPlanMap(freeTrialPlanList, campaign_code));
  loader.decorate('loadByPersist', loadSelectByPersistForBuyTrial(persist));
  loader.decorate('loadByShoppingCart', loadByShoppingCart);
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQuery());
  const planList = await loader.dispatch();
  return {
    allPlanMap: planMap,
    planMapFlat: planList,
    ...(isZpAicBuyTrial && errorMessage ? { ineligible: true, ineligibleMessage: errorMessage } : {}),
  };
};

const checkOutSubmit = async (submitParams) => {
  const { campaign_code: campaignCode = '' } = pageConfig || {};
  const getters = store.getters;
  const addressInfo = getters.addressInfo;
  const { apmInfo } = getters;
  const preview = submitParams?.preview ?? false;
  const payload = store.getters.orderDataPayload;
  let { paymentResponse, paymentGateway } = getters.paymentMethodInfo;
  const freeTrialPlan = payload?.planList?.find((plan) => !!plan.trialCode);
  const isProFreeTrial = [...zoomOneProMonthlyPlanCodes, ...zoomOneProYearlyPlanCodes].includes(freeTrialPlan?.planCode);
  // LOOKUP router
  const lookupRouterParams = getLookupRouterParams();

  if (apmInfo.isApm) {
    paymentResponse = JSON.stringify({
      integrationType: APM,
    });
    const eventResult = await pollingEventStatus({ rps: true });
    store.commit('setTransactionId', eventResult?.uniqueKey);
    store.commit('setPreviewKey', eventResult?.previewKey);
    const successParams = {
      ...lookupRouterParams,
      integrationType: APM,
      apmMethodName: apmInfo.apmPaymentMethodName,
    };
    apmInfo.apmPaymentMethodName === WISE
      ? gotoWiseSuccessPage({ wiseStateValue: apmInfo.wiseStateValue })
      : gotoSuccessPage(successParams);
    return {
      eventResult,
    };
  }

  const partnerReferParams = getPartnershipParams();
  // if the value is empty, will not send this field
  const extensionData = _.omitBy({
    freeTrialInstanceId: pageConfig?.ftId,
    ...partnerReferParams,
  }, isEmptyVal);
  const isARLChecked = getters.isARLChecked;
  let params = {
    taxInfo: getters.taxInfo,
    contact: cloneDeep([removeEmptyValue(getters.billToContact), removeEmptyValue(getters.soldToContact)]),
    paymentInfo: { paymentResponse, paymentGateway },
    orderData: payload,
    extension: {
      usageType: addressInfo?.usageType,
      useCase: addressInfo?.useCase,
      otherUseCase: addressInfo?.otherUseCase,
      campaignCode,
      trialCode: freeTrialPlan?.trialCode || '',
      scheduledEmailId: window.$cookie.get(ABANDON_CART_EMAIL_COOKIE_KEY),
      freeTrialSource: qs.parse(location.search.slice(1))['itm_source'] || 'Direct Link',
      ...(isARLChecked ? { isARLChecked: true } : {}),
      ...extensionData,
    },
    preview,
    orderSummary: {
      orderSummaryFields: getOrderSummaryFields(getters),
    }

  };

  const preAssignPhoneNumber = getPreAssignPhoneNumber();
  if (!isEmptyVal(preAssignPhoneNumber)) {
    params.extension.preAssignPhoneNumber = preAssignPhoneNumber;
  }

  isDebug && console.log('buy trial submit params: ', params);
  const isLoggedIn = window.SB?.loggedIn;
  addParamsIfApm(params);
  addParamsIfReferralProgram(params);
  if (isLoggedIn) {
    if (useBuyNowApiWhenCheckout()) {
      await freeUpgrade(params)
    } else {
      await buyFreeTrialPlansForFreeUser(params)
    }
  } else {
    if (useBuyNowApiWhenCheckout()) {
      await buySubscription({
        ...lookupRouterParams,
        ...params
      })
    } else {
      await buyFreeTrialPlans({
        ...lookupRouterParams,
        ...params
      })
    }
  }

  const eventResult = await pollingEventStatus({ rps: true });
  store.commit('setTransactionId', eventResult?.uniqueKey);
  store.commit('setPreviewKey', eventResult?.previewKey);
  if (window?.$context?.is.modal && modalHybrid.isInWebview && window?.$context?.is.buyTrial) {
    // * notifyFreeTrialStatus
    modalHybrid.notifyFreeTrialStatus();
  }
  !isLoggedIn || isProFreeTrial /* Pro free trial only allowed the free users to purchase and couldn't be ADD flow */ ? buyTrialSuccessPage(lookupRouterParams) : addTrialSuccessPage();

  return {
    eventResult,
  };
};

// if buy scheduler free trial, and added zoom workplace pro, then use buynow api to checkout
// Currently the judge condition is fixed to scheduler freetrial with meeting pro, may use a more general condition in the future
const useBuyNowApiWhenCheckout = () => {
  const { getSelectedPlans } = store.getters;
  return plansof(getSelectedPlans).type(planTypes.ZOOM_SCHEDULER).freeTrial().has() && plansof(getSelectedPlans).type(planTypes.ZOOM_MEETING).not().freeTrial().has()
}

const isBonsaiCampaign = () => pageConfig?.campaign_code === CAMPAIGN_CODES.BONSAI;

const resolveView = () => {
  if (store.getters.ineligible) return VIEWS.INELIGIBLE;
  // BONSAI: sign up only, no config / address / payment (plan data from getFreeTrialPlansByCampaignName, category e.g. bonsai_basic)
  if (isBonsaiCampaign()) return VIEWS.SIGN;
  return VIEWS.CONFIG;
};

const preCheck = async () => {
  // FREE TRIAL - check billing cycle same with base plan for future auto upgrade
  await validAddonCycleForBase();
};

export default {
  [flowTypes.buyTrial]: class extends Flow {
    view() {
      return resolveView();
    }
    init() {
      initForCoupon()
      return initShoppingCart(...arguments);
    }
    beforeSubmit() {
      return preCheck();
    }
    submit(submitParams) {
      return checkOutSubmit(submitParams);
    }
  },
};
